import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Mic, Users, ShieldCheck, GitCompare, Check, UserPlus, X, ScanSearch } from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth-context";
import { RecommendationPill } from "../components/Scorecards/Scorecards";
import { AddCandidateToRoleDialog } from "../components/PipelineAssociationDialogs";
import EditableTitle from "../components/EditableTitle";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

const STROKE = 1.5;

const STATUS_STYLE = {
  scheduled: "bg-ink-100 text-ink-700 border-ink-200",
  recording: "bg-amber-50 text-amber-800 border-amber-200",
  transcribed: "bg-emerald-50 text-emerald-800 border-emerald-800/15",
  reviewed: "bg-emerald-50 text-emerald-800 border-emerald-800/15",
};

export default function RoleDetail() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { member } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pipelineRecs, setPipelineRecs] = useState({});
  const [selected, setSelected] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const canManagePipeline = ["recruiter", "admin"].includes(member?.role);

  const loadPipeline = () => {
    api.get(`/roles/${roleId}/pipeline`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load role"))
      .finally(() => setLoading(false));
  };

  const confirmRemove = async () => {
    if (!removeTarget || removing) return;
    setRemoving(true);
    try {
      const { data: res } = await api.delete(`/roles/${roleId}/candidates/${removeTarget.candidate_id}`);
      toast.success(
        res.interviews_kept > 0
          ? `Removed from pipeline. ${res.interviews_kept} interview${res.interviews_kept === 1 ? "" : "s"} kept and still on the candidate profile.`
          : "Removed from pipeline.",
      );
      setRemoveTarget(null);
      loadPipeline();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove candidate");
    } finally {
      setRemoving(false);
    }
  };

  const toggleSelect = (cid) =>
    setSelected((prev) =>
      prev.includes(cid)
        ? prev.filter((x) => x !== cid)
        : prev.length >= 4
          ? prev
          : [...prev, cid],
    );

  useEffect(() => {
    api
      .get(`/roles/${roleId}/pipeline`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load role"))
      .finally(() => setLoading(false));
    // Phase 4a — per-candidate recommendation pills for the pipeline view
    api
      .get(`/roles/${roleId}/pipeline/recommendations`)
      .then(({ data }) => setPipelineRecs(data || {}))
      .catch(() => { /* silent for viewers without scorecards */ });
  }, [roleId]);

  if (loading) {
    return <AppShell><div className="flex justify-center p-12"><Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} /></div></AppShell>;
  }
  if (err || !data) {
    return <AppShell><div className="hp-card p-6 text-red-700" data-testid="role-error">{err}</div></AppShell>;
  }
  const { role, rounds, total_interviews, candidates } = data;
  const pipelineCandidates = data.pipeline_candidates || [];
  const memberIds = pipelineCandidates.map((p) => p.candidate.candidate_id);

  return (
    <AppShell>
      <div data-testid="role-detail-page" className="space-y-8">
        <header>
          <button onClick={() => navigate("/roles")} className="inline-flex items-center gap-1 text-[13px] text-ink-500 hover:text-ink-900" data-testid="back-to-roles">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={STROKE} /> All roles
          </button>
          <h1 className="font-display mt-3 text-3xl font-medium tracking-tight text-ink-900">
            <EditableTitle
              value={role.title}
              canEdit={canManagePipeline}
              testid="role-title"
              placeholder="Untitled role"
              onSave={async (next) => {
                const { data: updated } = await api.patch(`/roles/${role.role_id}`, { title: next });
                setData((prev) => ({ ...prev, role: { ...prev.role, ...updated } }));
                toast.success("Role renamed");
              }}
            />
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[13px] text-ink-600">
            {role.department && <span>{role.department}</span>}
            <span className="text-ink-400">·</span>
            <span className="capitalize">{role.status}</span>
            <span className="text-ink-400">·</span>
            <span><Users className="inline h-3.5 w-3.5 text-ink-400 mr-0.5" strokeWidth={STROKE} /> {candidates.length} candidate{candidates.length === 1 ? "" : "s"}</span>
            <span className="text-ink-400">·</span>
            <span><Mic className="inline h-3.5 w-3.5 text-ink-400 mr-0.5" strokeWidth={STROKE} /> {total_interviews} interview{total_interviews === 1 ? "" : "s"}</span>
          </div>
          {canManagePipeline && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-900"
                data-testid="role-add-candidate"
              >
                <UserPlus className="h-3.5 w-3.5" strokeWidth={STROKE} />
                Add candidate
              </button>
              <button
                onClick={() => navigate(`/roles/${roleId}/screen`)}
                className="inline-flex items-center gap-2 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-[13px] font-medium text-ink-700 hover:border-emerald-800/40 hover:text-emerald-900"
                data-testid="role-screen-cvs"
              >
                <ScanSearch className="h-3.5 w-3.5" strokeWidth={STROKE} />
                Screen CVs for this role
              </button>
            </div>
          )}
        </header>

        {/* Pipeline member cards (explicit ∪ implicit). Compact per-candidate
            cards: name, sourced badge, role-scoped interview count, one row
            per interview (round · date · per-rater pills, NO aggregation). */}
        {pipelineCandidates.length > 0 && (
          <section data-testid="pipeline-members">
            <div className="hp-section-label mb-3">In pipeline · {pipelineCandidates.length}</div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {pipelineCandidates.map(({ candidate, has_interviews, interview_count, interviews = [] }) => (
                <div
                  key={candidate.candidate_id}
                  className="hp-card flex flex-col p-4"
                  data-testid={`pipeline-member-${candidate.candidate_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link
                        to={`/candidates/${candidate.candidate_id}`}
                        className="block truncate text-[14px] font-medium text-ink-900 hover:text-emerald-800"
                        data-testid={`pipeline-card-name-${candidate.candidate_id}`}
                      >
                        {candidate.name}
                      </Link>
                      <div className="mt-0.5 flex items-center gap-2 text-[12px] text-ink-500">
                        {!has_interviews ? (
                          <span
                            className="rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-800"
                            title="Sourced — no interviews recorded yet"
                            data-testid={`pipeline-card-sourced-${candidate.candidate_id}`}
                          >
                            sourced
                          </span>
                        ) : (
                          <span data-testid={`pipeline-card-count-${candidate.candidate_id}`}>
                            {interview_count} interview{interview_count === 1 ? "" : "s"} for this role
                          </span>
                        )}
                      </div>
                    </div>
                    {canManagePipeline && (
                      <button
                        onClick={() => setRemoveTarget(candidate)}
                        className="shrink-0 rounded-full p-1 text-ink-400 hover:bg-red-50 hover:text-red-600"
                        data-testid={`pipeline-remove-${candidate.candidate_id}`}
                        aria-label={`Remove ${candidate.name} from role`}
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={STROKE} />
                      </button>
                    )}
                  </div>
                  {interviews.length > 0 && (
                    <div className="mt-3 divide-y divide-ink-100 border-t border-ink-100">
                      {interviews.map((iv) => (
                        <Link
                          key={iv.interview_id}
                          to={`/interviews/${iv.interview_id}`}
                          className="flex items-center gap-2 py-2 text-[12.5px] hover:bg-ink-50"
                          data-testid={`pipeline-card-interview-${iv.interview_id}`}
                        >
                          <span className="shrink-0 font-medium text-ink-700">R{iv.round_number}</span>
                          <span className="shrink-0 text-ink-400">·</span>
                          <span className="shrink-0 text-ink-500">{new Date(iv.created_at).toLocaleDateString()}</span>
                          <span className="ml-auto flex flex-wrap items-center justify-end gap-1">
                            {iv.scorecards.length === 0 ? (
                              <span className="text-[11px] text-ink-400">—</span>
                            ) : (
                              <>
                                {iv.scorecards.slice(0, 3).map((sc) => (
                                  <RecommendationPill
                                    key={sc.scorecard_id}
                                    recommendation={sc.recommendation}
                                    status={sc.status}
                                    fillerName={sc.filled_by_name}
                                  />
                                ))}
                                {iv.scorecards.length > 3 && (
                                  <span
                                    className="inline-flex items-center rounded-md border border-ink-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-ink-600"
                                    title={iv.scorecards.slice(3).map((sc) => `${sc.filled_by_name || "—"} · ${sc.recommendation || "draft"}`).join("\n")}
                                    data-testid={`pipeline-card-more-${iv.interview_id}`}
                                  >
                                    +{iv.scorecards.length - 3}
                                  </span>
                                )}
                              </>
                            )}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {candidates.length >= 2 && (
          <div className="hp-card p-4" data-testid="compare-selector">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="hp-section-label">Compare candidates</div>
                <p className="text-[12px] text-ink-500">Select 2–4 to lay them out side by side — scorecards, topics & quotes.</p>
              </div>
              <button
                disabled={selected.length < 2 || selected.length > 4}
                onClick={() => navigate(`/roles/${roleId}/compare?candidate_ids=${selected.join(",")}`)}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="compare-candidates-button"
              >
                <GitCompare className="h-3.5 w-3.5" strokeWidth={STROKE} />
                Compare{selected.length ? ` (${selected.length})` : ""}
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {candidates.map((c) => {
                const on = selected.includes(c.candidate_id);
                const full = !on && selected.length >= 4;
                return (
                  <button
                    key={c.candidate_id}
                    onClick={() => toggleSelect(c.candidate_id)}
                    disabled={full}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12.5px] font-medium transition ${
                      on
                        ? "border-emerald-800 bg-emerald-50 text-emerald-900"
                        : "border-ink-200 bg-white text-ink-700 hover:border-emerald-800/40"
                    } ${full ? "cursor-not-allowed opacity-40" : ""}`}
                    data-testid={`compare-select-${c.candidate_id}`}
                  >
                    <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-sm border ${on ? "border-emerald-800 bg-emerald-800" : "border-ink-300"}`}>
                      {on && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                    </span>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {rounds.length === 0 && (
          <div className="hp-card flex flex-col items-center justify-center p-12 text-center" data-testid="role-pipeline-empty">
            <Mic className="h-6 w-6 text-ink-300" strokeWidth={STROKE} />
            <p className="mt-3 text-[14px] text-ink-500">No interviews recorded for this role yet.</p>
          </div>
        )}

        {rounds.map((round) => (
          <section key={round.round_number} data-testid={`round-${round.round_number}`}>
            <div className="hp-section-label mb-3">Round {round.round_number} · {round.entries.length} interview{round.entries.length === 1 ? "" : "s"}</div>
            <div className="hp-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-ink-50 border-b border-ink-200">
                  <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-ink-500">
                    <th className="px-5 py-3">Candidate</th>
                    <th className="px-5 py-3">Interview</th>
                    <th className="px-5 py-3">Recommendation</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {round.entries.map(({ interview, candidate }) => (
                    <tr key={interview.interview_id} onClick={() => navigate(`/interviews/${interview.interview_id}`)}
                        className="hover:bg-ink-50 transition cursor-pointer"
                        data-testid={`pipeline-entry-${interview.interview_id}`}>
                      <td className="px-5 py-3 text-[14px] font-medium text-ink-900">
                        {candidate ? (
                          <Link to={`/candidates/${candidate.candidate_id}`} onClick={(e) => e.stopPropagation()} className="hover:text-emerald-800">
                            {candidate.name}
                          </Link>
                        ) : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="px-5 py-3 text-[14px] text-ink-700">
                        <span className="flex items-center gap-2">
                          {interview.consent_obtained && <ShieldCheck className="h-3.5 w-3.5 text-emerald-800" strokeWidth={STROKE} />}
                          {interview.title || "Untitled"}
                        </span>
                      </td>
                      {/* Phase 4a — inline recommendation pills (NOT pre-aggregated) */}
                      <td className="px-5 py-3" onClick={(e) => e.stopPropagation()} data-testid={`pipeline-recommendations-${interview.interview_id}`}>
                        {candidate && pipelineRecs[candidate.candidate_id]?.length > 0 ? (
                          <div className="flex flex-wrap gap-1 items-center max-w-[260px]">
                            {pipelineRecs[candidate.candidate_id].slice(0, 3).map((rec) => (
                              <RecommendationPill
                                key={rec.scorecard_id}
                                recommendation={rec.recommendation}
                                status={rec.status}
                                fillerName={rec.filled_by_name}
                              />
                            ))}
                            {pipelineRecs[candidate.candidate_id].length > 3 && (
                              <span
                                className="inline-flex items-center rounded-md border border-ink-200 bg-white px-2 py-0.5 text-[11px] font-medium text-ink-600"
                                title={pipelineRecs[candidate.candidate_id].slice(3).map(r => `${r.filled_by_name} · ${r.recommendation}`).join("\n")}
                              >
                                +{pipelineRecs[candidate.candidate_id].length - 3}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[11px] text-ink-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[interview.status] || "bg-ink-100 text-ink-700 border-ink-200"}`}>
                          {interview.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-[12px] text-ink-500">{new Date(interview.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3 text-right text-[13px] text-emerald-800">Open →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
      <AddCandidateToRoleDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        roleId={roleId}
        existingIds={memberIds}
        onAdded={loadPipeline}
      />
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => { if (!o && !removing) setRemoveTarget(null); }}>
        <AlertDialogContent data-testid="remove-from-role-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl font-medium">
              Remove {removeTarget?.name} from this role?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-ink-600">
              This removes the pipeline association only. Any recorded interviews for this
              candidate on this role are <strong>kept</strong> and stay reachable from the
              candidate&rsquo;s profile. The candidate is not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing} data-testid="remove-from-role-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmRemove(); }}
              disabled={removing}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
              data-testid="remove-from-role-confirm"
            >
              {removing ? "Removing…" : "Remove from role"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
